import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useRef } from "react";
import { toast } from "sonner";
import { TrashSimple } from "@phosphor-icons/react";

import { PageHeader } from "@/components/layout/heading";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

import { ProfileApiFactory } from "@/apis/profile";
import { UploadApiFactory } from "@/apis/upload";
import { useSession } from "@/hooks/use-session";
import { useAuthenticationStore } from "@/store/authentication";
import { mImageExtensions, mImageFormats, mMaxFileSize, mMaxFileSizeMB } from "@/constants/media-assets";
import { mRoles } from "@/constants/brand-onboarding";
import { unwrapApiError } from "@/lib/error";

const ProfileFormSchema = z.object({
  name: z.string().min(1, "Full name is required"),
  email: z.string().email("Please enter a valid email address"),
  company: z.string().optional(),
  role: z.string().optional(),
  image: z.string().optional(),
});

export const Route = createFileRoute("/dashboard/settings/_layout/profile")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="min-h-full w-full @container">
      <PageHeader title="Profile" description="Manage your profile settings." className="mb-8 hidden lg:block" />
      <Profile />
    </div>
  );
}

function Profile() {
  const { user, profile } = useSession();

  const updateUser = useAuthenticationStore((store) => store.updateUser);
  const updateCustomerProfile = useAuthenticationStore((store) => store.updateCustomerProfile);

  const file$ = useRef<File | null>(null);
  const input$ = useRef<HTMLInputElement>(null);

  const form = useForm({
    resolver: zodResolver(ProfileFormSchema),
    defaultValues: {
      name: user.name || "",
      email: user.email || "",
      image: user.image || "",
      role: profile.customer.job_title || "",
      company: profile.company.company_name || "",
    },
  });

  const { mutateAsync: updateUserProfile, isPending: isUpdatingUser } = useMutation({
    mutationFn: ProfileApiFactory.Apis.UpdateUserProfile,
  });

  const { mutateAsync: updateCustomerProfileApi, isPending: isUpdatingCustomer } = useMutation({
    mutationFn: ProfileApiFactory.Apis.UpdateCustomerProfile,
  });

  const { mutateAsync: uploadFile, isPending: isUploading } = useMutation({
    mutationFn: UploadApiFactory.Apis.UploadFile,
  });

  const handleChangeImage = (event: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!mImageFormats.includes(file.type)) {
      toast.error("Unsupported image format", { description: mImageExtensions.join(", ") + " images are supported" });
      return;
    }

    if (file.size > mMaxFileSize) {
      toast.error("Image is too large", { description: `Please upload an image smaller than ${mMaxFileSize}MB` });
      return;
    }

    file$.current = file;
    onChange(URL.createObjectURL(file));
  };

  const handleRemoveImage = async () => {
    form.setValue("image", "");
    file$.current = null;
    if (input$.current) input$.current.value = "";
    if (file$.current) URL.revokeObjectURL(file$.current);
  };

  const onSubmit = async (data: z.infer<typeof ProfileFormSchema>) => {
    try {
      let image = data.image;

      if (file$.current) {
        const response = await uploadFile(file$.current);
        image = response.data.file_url;
        form.setValue("image", image);
      }

      await Promise.all([
        updateUserProfile({ name: data.name, email: data.email, image: image || "" }).then((response) => updateUser(response.data)),
        updateCustomerProfileApi({ job_title: data.role || null }).then((response) => updateCustomerProfile(response.data)),
      ]);

      file$.current = null;
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(unwrapApiError(error, "Failed to update profile"));
    }
  };

  const handleCancel = () => {
    const image = form.getValues("image");
    form.reset();
    file$.current = null;
    if (image) URL.revokeObjectURL(image);
    if (input$.current) input$.current.value = "";
  };

  const isPending = isUpdatingUser || isUpdatingCustomer || isUploading;

  return (
    <Card className="border-none lg:border py-0 lg:py-6">
      <CardHeader className="sr-only">
        <CardTitle>Personal Information</CardTitle>
        <CardDescription>Update your personal details and profile picture</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 px-0 lg:px-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="image"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-6">
                    <Avatar className="size-24 border group relative">
                      <AvatarImage src={field.value} className="object-contain" />
                      <AvatarFallback className="bg-primary text-primary-foreground text-2xl uppercase">{user.name.slice(0, 2)}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col gap-y-1.5">
                      <FormControl>
                        <input ref={input$} type="file" accept={mImageFormats.join(",")} onChange={(e) => handleChangeImage(e, field.onChange)} className="hidden" />
                      </FormControl>
                      <div className="flex items-center gap-x-1.5">
                        <Button type="button" variant="secondary" size="sm" disabled={isPending} onClick={() => input$.current?.click()}>
                          Change Photo
                        </Button>
                        {field.value ? (
                          <Button type="button" variant="destructive" size="icon" disabled={isPending} onClick={handleRemoveImage} className="size-8">
                            <TrashSimple weight="bold" className="size-4" />
                          </Button>
                        ) : null}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {mImageExtensions.join(", ")}. Max <span className="font-medium">{mMaxFileSizeMB}MB</span>
                      </p>
                      <FormMessage />
                    </div>
                  </div>
                </FormItem>
              )}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={isPending} />
                    </FormControl>
                    <FormMessage>What is your full name?</FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} disabled />
                    </FormControl>
                    <FormMessage>What is your email address?</FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                disabled
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input {...field} disabled />
                    </FormControl>
                    <FormMessage>What is your company name?</FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select job title" />
                        </SelectTrigger>
                        <SelectContent>
                          {mRoles.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage>What best describes your job?</FormMessage>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col-reverse md:flex-row justify-start md:justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
